(function (factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.HarrodPackMath = api;
})(function () {
  'use strict';

  function positiveInteger(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
  }

  function parsePackSize(value) {
    if (typeof value !== 'string') return null;
    var text = value.trim().replace(/\s+/g, ' ');
    var discount = text.match(/\s*[-–—]\s*(\d+(?:\.\d+)?)%\s+off$/i);
    if (discount) {
      var percent = Number(discount[1]);
      if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null;
      text = text.slice(0, discount.index).trim();
    }
    var number = '([1-9]\\d{0,2}(?:,\\d{3})+|[1-9]\\d*)';
    var patterns = [
      new RegExp('^' + number + '$'),
      new RegExp('^(?:pack|bundle) of ' + number + '(?: (?:plants?|whips?))?$', 'i'),
      new RegExp('^' + number + '(?:[ -](?:plants?|whips?)(?: (?:pack|bundle))?|[ -](?:pack|bundle))$', 'i')
    ];
    for (var index = 0; index < patterns.length; index += 1) {
      var match = text.match(patterns[index]);
      if (match) {
        var count = Number(match[1].replace(/,/g, ''));
        return positiveInteger(count) ? count : null;
      }
    }
    return null;
  }

  function recommend(neededPlants, variants) {
    if (!positiveInteger(neededPlants) || !Array.isArray(variants)) return null;
    var packs = variants.filter(function (variant) {
      if (!variant || variant.available !== true || !positiveInteger(variant.packSize)) return false;
      var minimum = variant.min == null ? 1 : variant.min;
      var increment = variant.step == null ? 1 : variant.step;
      var maximum = variant.max == null ? null : variant.max;
      return positiveInteger(minimum) && positiveInteger(increment) &&
        (maximum === null || (positiveInteger(maximum) && maximum >= minimum)) &&
        positiveInteger(minimum * variant.packSize) && positiveInteger(increment * variant.packSize);
    }).map(function (variant) {
      return { variant: variant, size: variant.packSize, min: variant.min == null ? 1 : variant.min,
        step: variant.step == null ? 1 : variant.step, max: variant.max == null ? null : variant.max };
    }).sort(function (left, right) {
      if (left.size !== right.size) return right.size - left.size;
      var leftId = String(left.variant.id == null ? '' : left.variant.id);
      var rightId = String(right.variant.id == null ? '' : right.variant.id);
      return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
    });
    if (!packs.length) return null;

    var single = null;
    packs.forEach(function (pack, index) {
      var variant = pack.variant;
      var requiredPacks = Math.ceil(neededPlants / variant.packSize);
      var increments = Math.ceil(Math.max(0, requiredPacks - pack.min) / pack.step);
      var packQuantity = pack.min + increments * pack.step;
      var suppliedPlants = packQuantity * variant.packSize;
      if (!positiveInteger(packQuantity) || !positiveInteger(suppliedPlants)) return;
      if (pack.max !== null && packQuantity > pack.max) return;
      if (suppliedPlants < neededPlants) return;
      var quantities = packs.map(function () { return 0; });
      quantities[index] = packQuantity;
      var candidate = { quantities: quantities, count: packQuantity, supplied: suppliedPlants };
      if (!single || candidate.supplied < single.supplied ||
        (candidate.supplied === single.supplied && preferred(candidate, single))) single = candidate;
    });
    if (packs.length === 1) return single ? result(single, packs, neededPlants) : null;

    var divisor = packs.reduce(function (value, pack) { return gcd(value, pack.size); }, packs[0].size);
    // Removing the last increment (or the initial minimum) from any minimal
    // covering combination must put it below the target. This bounds surplus.
    var largestJump = packs.reduce(function (value, pack) {
      return Math.max(value, pack.size * Math.max(pack.min, pack.step));
    }, 0);
    var upper = neededPlants > Number.MAX_SAFE_INTEGER - largestJump + 1 ? Number.MAX_SAFE_INTEGER : neededPlants + largestJump - 1;
    if (single) upper = Math.min(upper, single.supplied);
    var limit = Math.floor(upper / divisor);
    var target = Math.ceil(neededPlants / divisor);
    // Far above the storefront's 60,000-plant limit, fail closed rather than
    // block the browser or return an unverified mixture.
    if (limit > 200000) return null;
    var states = new Array(limit + 1);
    states[0] = { quantities: packs.map(function () { return 0; }), count: 0 };

    packs.forEach(function (pack, index) {
      var width = pack.size / divisor;
      var initialWidth = pack.min * width;
      if (initialWidth > limit) return;
      var using = new Array(limit + 1);
      for (var sum = initialWidth; sum <= limit; sum += 1) {
        var previous = states[sum - initialWidth];
        if (!previous) continue;
        using[sum] = extend(previous, index, pack.min);
      }
      var extraLimit = Math.floor((limit - initialWidth) / (pack.step * width));
      if (pack.max !== null) extraLimit = Math.min(extraLimit, Math.floor((pack.max - pack.min) / pack.step));
      // Binary groups represent every allowed increment count while avoiding
      // a loop over every possible pack quantity for every subtotal.
      for (var group = 1; extraLimit > 0; group *= 2) {
        var incrementsInGroup = Math.min(group, extraLimit);
        var quantity = incrementsInGroup * pack.step;
        var weight = quantity * width;
        for (var total = limit; total >= initialWidth + weight; total -= 1) {
          var source = using[total - weight];
          if (!source) continue;
          var combined = extend(source, index, quantity);
          if (!using[total] || preferred(combined, using[total])) using[total] = combined;
        }
        extraLimit -= incrementsInGroup;
      }
      for (var subtotal = initialWidth; subtotal <= limit; subtotal += 1) {
        if (using[subtotal] && (!states[subtotal] || preferred(using[subtotal], states[subtotal]))) states[subtotal] = using[subtotal];
      }
    });
    for (var supplied = target; supplied <= limit; supplied += 1) {
      if (states[supplied]) {
        states[supplied].supplied = supplied * divisor;
        return result(states[supplied], packs, neededPlants);
      }
    }
    return null;
  }

  function gcd(left, right) {
    while (right) { var remainder = left % right; left = right; right = remainder; }
    return left;
  }

  function extend(previous, index, quantity) {
    var quantities = previous.quantities.slice();
    quantities[index] += quantity;
    return { quantities: quantities, count: previous.count + quantity };
  }

  function preferred(left, right) {
    if (left.count !== right.count) return left.count < right.count;
    for (var index = 0; index < left.quantities.length; index += 1) {
      if (left.quantities[index] !== right.quantities[index]) return left.quantities[index] > right.quantities[index];
    }
    return false;
  }

  function result(state, packs, neededPlants) {
    var lines = [];
    state.quantities.forEach(function (quantity, index) {
      if (quantity) lines.push({ variant: packs[index].variant, packSize: packs[index].size,
        packQuantity: quantity, suppliedPlants: quantity * packs[index].size });
    });
    var recommendation = { lines: lines, packQuantity: state.count, suppliedPlants: state.supplied,
      extraPlants: state.supplied - neededPlants };
    // Single-line callers can continue using the existing convenience fields.
    if (lines.length === 1) {
      recommendation.variant = lines[0].variant;
      recommendation.packSize = lines[0].packSize;
    }
    return recommendation;
  }

  function rowSpacing(rate, doubleRow) {
    if (doubleRow === undefined) doubleRow = false;
    if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0 || typeof doubleRow !== 'boolean') return null;
    var centimetres = (doubleRow ? 200 : 100) / rate;
    return Number.isFinite(centimetres) && centimetres > 0 ? centimetres : null;
  }

  return Object.freeze({ parsePackSize: parsePackSize, recommend: recommend, rowSpacing: rowSpacing });
});
